import type { ExecutionLogEntry } from '@/api/cockpit';
import { StructuredDataView } from '@/components/common/StructuredDataView';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  cn,
  formatPipelineStepLabel,
  formatRelativeTime,
  formatWorkflowStateLabel,
} from '@/lib/utils';
import { executionLogToneClass } from '../helpers';

export interface ActivityCardProps {
  executionLogs: readonly ExecutionLogEntry[];
  showAllExecutionLogs: boolean;
  onShowAllExecutionLogs: () => void;
}

export function ActivityCard(props: ActivityCardProps): JSX.Element {
  const visibleLogs = props.showAllExecutionLogs
    ? props.executionLogs
    : props.executionLogs.slice(0, 2);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
        <CardDescription>Recent changes for this order.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {props.executionLogs.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No activity has been recorded for this order.
          </div>
        ) : (
          <div className="relative">
            <div className="space-y-3">
              {visibleLogs.map((entry) => (
                <ExecutionLogEntryCard key={entry.id} entry={entry} />
              ))}
            </div>
            {!props.showAllExecutionLogs && props.executionLogs.length > 2 ? (
              <button
                type="button"
                className="absolute inset-x-0 bottom-0 flex h-20 items-end justify-center rounded-b-md bg-gradient-to-t from-background via-background/95 to-transparent pb-2 text-sm font-medium text-primary"
                onClick={props.onShowAllExecutionLogs}
              >
                Show {props.executionLogs.length - 2} more log entries
              </button>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExecutionLogEntryCard({ entry }: { entry: ExecutionLogEntry }): JSX.Element {
  return (
    <div className={cn('rounded-md border-l-4 border-y border-r p-3', executionLogToneClass(entry))}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {formatPipelineStepLabel(entry.substepId ?? entry.stage)}
          </Badge>
          <span className="text-sm font-medium">
            {formatWorkflowStateLabel(entry.fromState)} to {formatWorkflowStateLabel(entry.toState)}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">{formatRelativeTime(entry.emittedAt)}</div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Latency: {entry.latencyMs}ms</span>
        <span>
          Reason:{' '}
          <span className="font-semibold text-foreground">{entry.reason ?? 'none'}</span>
        </span>
        <span>Model: {entry.modelVersion ?? 'n/a'}</span>
      </div>
      {entry.detail ? (
        <StructuredDataView title="Details" content={entry.detail} className="mt-2" />
      ) : null}
    </div>
  );
}
